import {
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  subDays,
} from 'date-fns';
import posthog from 'posthog-js';
import { getDownloadURL, ref } from 'firebase/storage';
import { FilterOption, LoadedUser } from '../../interfaces';
import { storage, auth } from '../../firebase';
import { openExternalUrl } from '../../utils/global/helper';

export function getFullName({
  firstName,
  lastName,
}: {
  firstName?: string | null;
  lastName?: string | null;
}): string {
  return `${firstName || ''} ${lastName || ''}`.trim();
}

// Turn label and action into an object for Modal Footer
export function createFooterAction(label: string, action: any) {
  const footerAction = {
    label,
    action,
  };

  return footerAction;
}

export function cx(...args: unknown[]) {
  return args
    .flat()
    .filter(x => typeof x === 'string')
    .join(' ')
    .trim();
}

export function getUserNameFromBusiness(
  loadedUser: LoadedUser | undefined,
  isCustomer: boolean,
  userId?: string | null,
  lowercase = false,
) {
  if (isCustomer) return lowercase ? 'customer' : 'Customer';

  if (!userId) return '';

  const employee = loadedUser?.business.employees.find(
    e => e.employeeId === userId,
  );

  return employee
    ? employee.displayName ||
        getFullName({
          firstName: employee.user?.firstName,
          lastName: employee.user?.lastName,
        })
    : lowercase
    ? 'unknown'
    : 'Unknown';
}

export function trimString(str: string, length: number) {
  return str.length > length ? `${str.slice(0, length)}...` : str;
}

/**
 * Check if the date is the same day or before the date to compare
 * @param date - The date to check
 * @param dateToCompare - The date to compare to
 * @returns True if the date is the same day or before the date to compare, false otherwise
 */
export function isSameDayOrBefore(date: Date, dateToCompare: Date) {
  return isSameDay(date, dateToCompare) || isBefore(date, dateToCompare);
}

/**
 * Check if the date is the same day or after the date to compare
 * @param date - The date to check
 * @param dateToCompare - The date to compare to
 * @returns True if the date is the same day or after the date to compare, false otherwise
 */
export function isSameDayOrAfter(date: Date, dateToCompare: Date) {
  return isSameDay(date, dateToCompare) || isAfter(date, dateToCompare);
}

export function isCuid(value: string) {
  return /^c[a-z0-9]{24}$/.test(value);
}

/**
 * Returns true if the URL is a Firebase/Google Cloud Storage URL that still
 * needs a token (i.e. does not already have both token and alt params).
 * Only these URLs should be passed to getStorageUrl.
 */
export function needsStorageUrl(url: string): boolean {
  try {
    const u = new URL(url);
    if (!u.hostname.endsWith('googleapis.com')) return false;
    return !(u.searchParams.has('token') && u.searchParams.get('alt'));
  } catch {
    return false;
  }
}

export async function getStorageUrl(url: string) {
  const urlObj = new URL(url);

  if (urlObj.searchParams.has('token') && urlObj.searchParams.get('alt')) {
    return url;
  }

  try {
    const urlNoParams = urlObj.toString().split('?')[0];
    const storageRef = ref(storage, urlNoParams);
    const downloadUrl = await getDownloadURL(storageRef);
    return downloadUrl;
  } catch {
    return null;
  }
}

export function stripStorageToken(url: string) {
  try {
    const u = new URL(url);
    u.searchParams.delete('token');
    return u.toString();
  } catch {
    return url;
  }
}

export async function openStorageUrl(url: string) {
  const storageUrl = await getStorageUrl(url);
  if (!storageUrl) return;

  const response = await fetch(storageUrl);
  const blob = await response.blob();
  const newBlob = new Blob([blob], { type: blob.type });
  const blobObjectUrl = URL.createObjectURL(newBlob);

  // Create download link with filename
  openExternalUrl(blobObjectUrl);
}

export const getInitialFilterValue = (
  name: string,
  options: FilterOption[],
  type: 'MULTI' | 'SINGLE' | 'DATE_RANGE',
  defaultInitialValue?: string | boolean,
  isBoolean?: boolean,
) => {
  const defaultValue = type === 'MULTI' ? [] : undefined;

  const cachedValue = sessionStorage.getItem(name);

  let parsedCachedValue = cachedValue ? JSON.parse(cachedValue) : defaultValue;

  if (isBoolean && typeof parsedCachedValue === 'string') {
    parsedCachedValue = parsedCachedValue === 'true';
  }

  if (type === 'MULTI') {
    if (Array.isArray(parsedCachedValue) && parsedCachedValue.length > 0) {
      const filteredCachedList = parsedCachedValue.filter(value =>
        options.some(option => option.value === value),
      );
      if (filteredCachedList.length > 0) return filteredCachedList;
    }

    return options.map(option => option.value);
  }

  if (type === 'DATE_RANGE') {
    if (Array.isArray(parsedCachedValue) && parsedCachedValue.length === 2) {
      return [new Date(parsedCachedValue[0]), new Date(parsedCachedValue[1])];
    }
    return [startOfDay(subDays(new Date(), 7)), endOfDay(new Date())];
  }

  if (
    options.some(
      option =>
        option.value === parsedCachedValue ||
        (isBoolean && option.value === parsedCachedValue?.toString()),
    )
  ) {
    return parsedCachedValue;
  }

  return defaultInitialValue || defaultValue;
};

export function applyAlgoliaRestriction(
  filter: string,
  restrictedBusinessIds?: string[],
): string {
  if (!restrictedBusinessIds?.length) return filter;
  const suffix = restrictedBusinessIds
    .map(id => `NOT businessId:${id}`)
    .join(' AND ');
  return `(${filter}) AND ${suffix}`;
}

export async function handleSignOut() {
  posthog.reset();
  await auth.signOut();
}

export async function compressImage(
  file: File | Blob,
  {
    quality = 0.95,
    maxWidth = 1600,
    maxHeight = 1200,
    webp = false,
  }: {
    quality?: number;
    maxWidth?: number;
    maxHeight?: number;
    webp?: boolean;
  } = {},
): Promise<Blob> {
  if (
    file.type === 'image/jpeg' ||
    file.type === 'image/png' ||
    file.type === 'image/webp'
  ) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event: ProgressEvent<FileReader>) => {
        const img = new Image();
        img.src = event.target!.result as string; // Type assertion here

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Set the canvas dimensions to the desired image size

          let { width } = img;
          let { height } = img;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          // Draw the image on the canvas with the new dimensions
          ctx?.drawImage(img, 0, 0, width, height);

          // Convert the canvas content back to a compressed image
          canvas.toBlob(
            blob => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            webp ? 'image/webp' : file.type, // Set the image type
            quality, // Set the image quality (0.9 means 90% quality)
          );
        };
      };

      reader.onerror = reader_error => {
        reject(reader_error);
      };
    });
  }

  // For other image types, return the original file without compression
  return file;
}
